<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { useAuthStore } from '../stores/auth'
import { useBookingsStore, type Booking } from '../stores/bookings'

const authStore = useAuthStore()
const bookingsStore = useBookingsStore()
const router = useRouter()

const selectedBooking = ref<Booking | null>(null)
const showModal = ref(false)

// Redirect to invite page if user needs to activate
onMounted(async () => {
  if (authStore.needsInvite) {
    router.push('/invite')
    return
  }
  await bookingsStore.fetchBookings()
})

// Convert bookings to calendar events
const calendarEvents = computed(() => {
  return bookingsStore.approvedBookings.map(booking => ({
    id: booking.id,
    // Use "Reserved" for privacy-masked bookings (no user_name)
    title: booking.user_name ? (booking.title || `${booking.user_name}'s stay`) : 'Reserved',
    start: booking.check_in,
    end: addDays(booking.check_out, 1), // FullCalendar end date is exclusive
    backgroundColor: getEventColor(booking),
    borderColor: getEventColor(booking),
    extendedProps: { booking },
  }))
})

function getEventColor(booking: Booking): string {
  if (booking.user_id === authStore.user?.id) {
    return '#0ea5e9' // Primary blue for own bookings
  }
  // Gray for privacy-masked bookings, green for visible others
  return booking.user_name ? '#22c55e' : '#9ca3af'
}

function addDays(dateStr: string, days: number): string {
  const date = new Date(dateStr)
  date.setDate(date.getDate() + days)
  return date.toISOString().split('T')[0]
}

const calendarOptions = computed(() => ({
  plugins: [dayGridPlugin, interactionPlugin],
  initialView: 'dayGridMonth',
  headerToolbar: {
    left: 'prev,next today',
    center: 'title',
    right: 'dayGridMonth,dayGridWeek',
  },
  events: calendarEvents.value,
  eventClick: handleEventClick,
  dateClick: handleDateClick,
  height: 'auto',
}))

function handleEventClick(info: any) {
  selectedBooking.value = info.event.extendedProps.booking
  showModal.value = true
}

function handleDateClick(info: any) {
  if (authStore.canBook) {
    // Navigate to request page with pre-filled date
    router.push(`/request?check_in=${info.dateStr}`)
  }
}

function closeModal() {
  showModal.value = false
  selectedBooking.value = null
}

async function cancelBooking() {
  if (selectedBooking.value && confirm('Are you sure you want to cancel this booking?')) {
    await bookingsStore.cancelBooking(selectedBooking.value.id)
    closeModal()
  }
}
</script>

<template>
  <div class="max-w-7xl mx-auto p-4 md:p-6">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">Cabin Calendar</h1>
        <p class="text-gray-600">View availability and upcoming stays</p>
      </div>
      <router-link
        v-if="authStore.canBook"
        to="/request"
        class="btn btn-primary"
      >
        + Book a Stay
      </router-link>
    </div>

    <!-- Legend -->
    <div class="flex gap-4 mb-4 text-sm">
      <div class="flex items-center gap-2">
        <span class="w-4 h-4 rounded bg-primary-500"></span>
        <span>Your bookings</span>
      </div>
      <div class="flex items-center gap-2">
        <span class="w-4 h-4 rounded bg-green-500"></span>
        <span>Other bookings</span>
      </div>
    </div>

    <!-- Calendar -->
    <div class="card">
      <FullCalendar :options="calendarOptions" />
    </div>

    <!-- Booking Detail Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="closeModal"
    >
      <div class="card max-w-md w-full">
        <div class="flex justify-between items-start mb-4">
          <h2 class="text-xl font-bold text-gray-800">
            {{ selectedBooking?.user_name ? selectedBooking?.title : 'Reserved' }}
          </h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <!-- Full details for own bookings or when not masked -->
        <div v-if="selectedBooking?.user_name" class="space-y-3 text-gray-600">
          <p><strong>Guest:</strong> {{ selectedBooking?.user_name }}</p>
          <p><strong>Check-in:</strong> {{ selectedBooking?.check_in }} at {{ selectedBooking?.check_in_time }}</p>
          <p><strong>Check-out:</strong> {{ selectedBooking?.check_out }} at {{ selectedBooking?.check_out_time }}</p>
          <p><strong>Guests:</strong> {{ selectedBooking?.num_guests }}</p>
          <p v-if="selectedBooking?.notes"><strong>Notes:</strong> {{ selectedBooking?.notes }}</p>
        </div>

        <!-- Limited details for privacy-masked bookings -->
        <div v-else class="space-y-3 text-gray-600">
          <p class="text-gray-500">This time is reserved by another guest.</p>
          <p><strong>Check-in:</strong> {{ selectedBooking?.check_in }}</p>
          <p><strong>Check-out:</strong> {{ selectedBooking?.check_out }}</p>
        </div>

        <div
          v-if="selectedBooking?.user_id === authStore.user?.id || authStore.isOwner"
          class="mt-6 flex gap-3"
        >
          <button @click="cancelBooking" class="btn btn-danger flex-1">
            Cancel Booking
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
